/**
 * Module context utilities — every progress read/write must be scoped to a module
 * so that ENEM answers don't show up in the Concursos dashboard and vice-versa.
 */

export type ModuleType =
  | 'concursos'
  | 'enem'
  | 'oab'
  | 'tribunais'
  | 'policiais'
  | 'juridicas'
  | 'cnu'
  | 'bancario'
  | 'militares'
  | 'residencia'
  | 'vestibulares'
  | 'colegios'
  | 'videoaulas';

const ROUTE_PREFIX_TO_MODULE: Array<[string, ModuleType]> = [
  ['/enem', 'enem'],
  ['/oab', 'oab'],
  ['/tribunais', 'tribunais'],
  ['/carreiras-policiais', 'policiais'],
  ['/carreiras-juridicas', 'juridicas'],
  ['/cnu', 'cnu'],
  ['/bancario', 'bancario'],
  ['/militares', 'militares'],
  ['/residencia', 'residencia'],
  ['/vestibulares', 'vestibulares'],
  ['/colegios', 'colegios'],
  ['/videoaulas', 'videoaulas'],
];

/**
 * Maps a sidebar/module-folder key to the `module_type` value stored in the DB.
 * Sidebar uses 'carreiras-policiais' / 'carreiras-juridicas' but the DB enum
 * uses the shorter 'policiais' / 'juridicas'.
 */
const SIDEBAR_TO_DB: Record<string, ModuleType> = {
  'carreiras-policiais': 'policiais',
  'carreiras-juridicas': 'juridicas',
};

export function normalizeModule(value: string | null | undefined): ModuleType {
  if (!value) return 'concursos';
  if (SIDEBAR_TO_DB[value]) return SIDEBAR_TO_DB[value];
  return (value as ModuleType) || 'concursos';
}

/**
 * Resolves the current module from the URL path. Falls back to
 * localStorage('last_active_module') and finally to 'concursos'.
 */
export function getCurrentModule(pathname?: string): ModuleType {
  const path = pathname ?? (typeof window !== 'undefined' ? window.location.pathname : '/');

  for (const [prefix, mod] of ROUTE_PREFIX_TO_MODULE) {
    if (path === prefix || path.startsWith(prefix + '/')) return mod;
  }

  if (typeof window !== 'undefined') {
    const stored = localStorage.getItem('last_active_module');
    if (stored) return normalizeModule(stored);
  }
  return 'concursos';
}

/** Maps an exam_type UUID to the module it belongs to (mirror of the SQL function). */
export function moduleFromExamType(examTypeId: string | null | undefined): ModuleType {
  switch (examTypeId) {
    case 'f57ab166-ae14-4e6e-a443-d7966a686430': return 'enem';
    case '97030814-f28a-4065-8f4f-5fb9c7b3ade9': return 'oab';
    case '07b7d522-8235-4422-b7b7-29147d5bd819': return 'tribunais';
    case '06e26c7a-51ce-4e6a-9400-5332bbf921e6':
    case 'd7d65dfc-f328-4f7f-8602-e74a44b85513': return 'juridicas';
    case '35908822-8d8c-40a2-b55d-0b529fb08937':
    case '8f84beb5-0868-4bf1-a4db-05ffb1c8bc97': return 'policiais';
    case '199b2edb-461d-431e-8958-8b93ea4e5e36':
    case 'e1a415f2-8d3f-4168-b681-9ec960495cc3': return 'bancario';
    case '2277315b-358a-4373-a52f-02cdbdef862a': return 'militares';
    case '5b7c2b5f-9728-49fd-a364-49b3001f9a73': return 'residencia';
    case 'c1f0cb51-d55a-41c6-9509-4a8e6439ad45': return 'vestibulares';
    default: return 'concursos';
  }
}
